<template>
  <div>
    <div
      v-if="hasParentToken"
      class="pody-frame"
      :style="{ height: currentFrameHeight }"
    >
      <iframe
        v-if="isReady"
        :src="currentPodyUrl"
        loading="lazy"
        frameborder="0"
        @load="handleIframeLoad"
      />
    </div>
    <b-overlay
      v-else
      :show="true"
      style="height: 100vh;"
    />
    <!-- Leave Page Confirmation Modal -->
    <b-modal
      ref="leavePageModal"
      id="leave-page-modal"
      title="Leave site?"
      ok-title="Leave Page"
      centered
      size="sm"
      hide-header-close
      no-close-on-backdrop
      body-class="custom-body"
      cancel-title="Cancel"
      @ok="confirmLeave"
      @cancel="cancelLeave"
      @hide="cancelLeave"
    >
      <p>Changes you made may not be saved.</p>
    </b-modal>
  </div>
</template>

<script>
import { BOverlay, BModal } from 'bootstrap-vue'
import { createNamespacedHelpers } from 'vuex'
import { toastification } from '@core/mixins/toast'
import { handlePodyServices } from './pody-actions'

const { mapGetters, mapActions } = createNamespacedHelpers('adsAccount')
const { mapActions: mapActionsAuth } = createNamespacedHelpers('auth')

const ALLOWED_ROUTES = process.env.VUE_APP_PODY_ALLOWED_ROUTES?.split(',') ?? []
const TOKEN_CHECK_INTERVAL = 100 // 100ms
const MAX_RETRIES = 50 // 5 seconds total
const ROUTE_IGNORE_CHANGES = ['/creatives'] // these routes will be ignore when detect leaving page

export default {
  name: 'PodyFrame',
  components: {
    BOverlay,
    BModal,
  },
  mixins: [
    toastification,
  ],
  data() {
    return {
      isReady: false,
      retryCount: 0,
      tokenCheckInterval: null,
      pendingNavigation: null,
      childRoute: null,
    }
  },
  computed: {
    ...mapGetters(['listAccount']),
    currentPodyUrl() {
      const currentPath = this.$route.path.replace('/pody-', '')
      return `${process.env.VUE_APP_PODY_URL}/${currentPath}`
    },
    currentFrameHeight() {
      const currentHeight = window.innerHeight
      return `${currentHeight - 156}px`
    },
    hasParentToken() {
      return localStorage.getItem('id_token')
    },
  },
  mounted() {
    window.addEventListener('message', this.handleMessage)
    this.checkAndInitialize()
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleMessage)
    if (this.tokenCheckInterval) {
      clearInterval(this.tokenCheckInterval)
    }
  },
  beforeRouteLeave(to, from, next) {
    if (ROUTE_IGNORE_CHANGES.includes(this.childRoute)) {
      next()
      return
    }
    this.pendingNavigation = next
    this.$refs.leavePageModal.show()
  },
  methods: {
    ...mapActionsAuth([
      'logout',
    ]),
    ...mapActions([
      'getAccountList',
    ]),
    checkAndInitialize() {
      const token = localStorage.getItem('id_token')
      if (token) {
        this.isReady = true
        this.sendTokenToPody()
      } else {
        this.startTokenCheck()
      }
    },
    startTokenCheck() {
      this.tokenCheckInterval = setInterval(() => {
        const token = localStorage.getItem('id_token')
        if (token) {
          this.isReady = true
          clearInterval(this.tokenCheckInterval)
        } else {
          this.retryCount += 1
          if (this.retryCount >= MAX_RETRIES) {
            clearInterval(this.tokenCheckInterval)
          }
        }
      }, TOKEN_CHECK_INTERVAL)
    },
    confirmLeave() {
      if (this.pendingNavigation) {
        this.pendingNavigation()
        this.pendingNavigation = null
      }
    },
    cancelLeave() {
      if (this.pendingNavigation) {
        this.pendingNavigation(false)
        this.pendingNavigation = null
      }
    },
    handleIframeLoad() {
      this.sendTokenToPody()
      this.handleGetAdAccount()
    },
    sendTokenToPody() {
      const iframe = document.querySelector('.pody-frame iframe')
      const token = localStorage.getItem('id_token')

      if (!token) {
        return
      }

      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type: 'SET_TTM_TOKEN',
          data: {
            token,
            platform: 'ttm',
          },
        }, process.env.VUE_APP_PODY_URL)
      }
    },
    handleMessage(event) {
      if (event.origin !== process.env.VUE_APP_PODY_URL) return
      const { type, data } = event.data
      switch (type) {
        case 'navigation':
          this.handleNavigation(data)
          break
        case 'CHILD_LOGOUT':
          this.logout()
          break
        case 'GET_AD_ACCOUNT':
          this.handleGetAdAccount()
          break
        case 'ROUTE_CHILD_CHANGES':
          this.handleChildRouteChange(data)
          break
        case 'PODY_ACTIONS':
          this.handlePodyActions(data)
          break
        case 'CREATE_NEW_ADS_ACCOUNT':
          this.handleCreateNewAdAccount()
          break
        default:
          this.handleUnknowCase({ type, data })
      }
    },
    handleNavigation(data) {
      if (data.path) {
        const requestedPath = data.path.replace(/^\//, '')
        if (ALLOWED_ROUTES.includes(requestedPath)) {
          this.$router.push(`/pody/${requestedPath}`)
        }
      }
    },

    handleChildRouteChange({ path }) {
      this.childRoute = path
    },

    handleGetAdAccount() {
      this.getAccountList().then(() => {
        const activedListAccount = this.listAccount?.content?.filter(account => account.status === 'SHOW_ACCOUNT_STATUS_APPROVED' || account.status === 'SHOW_ACCOUNT_STATUS_IN_REVIEW') ?? []
        this.sendToPody('SET_AD_ACCOUNTS', {
          listAccount: activedListAccount,
        })
      })
    },

    handleCreateNewAdAccount() {
      window.open(`${process.env.VUE_APP_SITE_URL}/my-ads-account?tab=settings`)
    },

    async handlePodyActions(data) {
      try {
        this.sendToPody('SET_PARENT_API_LOADING', { type: data.type, loading: true })
        const response = await handlePodyServices(data)
        if (response.status === 200) {
          this.toastSuccess(`${data.type} creative(s) successfully `)
        }
      } catch (error) {
        // eslint-disable-next-line
        console.error('Debug error at api parent: ', error)
        if (error.data?.messageCode === 'AUTHENTICATION_FAILED' || error.data?.messageCode === 'TOKEN_NOT_FOUND') {
          this.logout()
          return
        }
        this.toastFailure(error.data?.message ?? 'Please try again')
      } finally {
        this.sendToPody('SET_PARENT_API_LOADING', { type: data.type, loading: false })
      }
    },

    sendToPody(type, data) {
      const iframe = document.querySelector('.pody-frame iframe')
      const token = localStorage.getItem('id_token')
      if (!token) {
        return
      }
      if (iframe && iframe.contentWindow) {
        iframe.contentWindow.postMessage({
          type,
          data,
        }, process.env.VUE_APP_PODY_URL)
      }
    },

    handleUnknowCase() {},
  },
}
</script>

<style lang="scss">
.pody-frame {
  width: 100%;
  height: 1600px;
  overflow: hidden;
  position: relative;
}

.pody-frame iframe {
  width: 100%;
  height: 100%;
  border: none;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.loading-state {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
#leave-page-modal {
  .modal-content {
    padding: 32px !important;
    border-radius: 32px !important;
  }

  .modal-header {
    padding: 0px !important;
  }

  .modal-body.custom-body {
    padding: 0px !important;
  }

  .modal-footer {
    border: none !important;
    padding: 0px !important;
  }

  .btn {
    padding: 4px 15px !important;
    border: none;
    min-height: 40px;
    background-color: #fff !important;
    color: #000 !important;
    &:hover {
      box-shadow: none !important;
      opacity: 0.8;
    }
  }

  .btn-primary {
    border: none;
    color: #fff !important;
    background: linear-gradient(128.33deg,var(--button-color-start) -29.89%,var(--button-color-end) 98.77%);
  }
}
</style>
